import React, { useState } from "react";
import {
  Paper, Typography, TextField, Button, Box, CircularProgress,
  InputAdornment, IconButton, Grid, Divider
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import { toast } from "react-toastify";
import api from "../../services/partnerApi";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(4),
    borderRadius: 20,
    background: "rgba(255,255,255,0.05)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255,255,255,0.1)",
    boxShadow: "0 25px 60px rgba(0,0,0,0.5)",
    color: "#fff",
    maxWidth: 480,
    width: "100%",
  },
  title: {
    fontWeight: 700,
    fontSize: 24,
    background: "linear-gradient(90deg, #f59e0b, #ef4444)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    marginBottom: 4,
  },
  input: {
    "& .MuiOutlinedInput-root": {
      color: "#fff",
      "& fieldset": { borderColor: "rgba(255,255,255,0.2)" },
      "&:hover fieldset": { borderColor: "#f59e0b" },
      "&.Mui-focused fieldset": { borderColor: "#f59e0b" },
    },
    "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.5)" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#f59e0b" },
    marginBottom: theme.spacing(2),
  },
  btn: {
    background: "linear-gradient(90deg, #f59e0b, #ef4444)",
    color: "#fff",
    fontWeight: 700,
    padding: theme.spacing(1.5),
    borderRadius: 10,
    fontSize: 15,
    marginTop: theme.spacing(1),
    "&:hover": { background: "linear-gradient(90deg, #d97706, #dc2626)" },
  },
  link: { color: "#f59e0b", cursor: "pointer", fontWeight: 600 },
  stepCard: {
    background: "rgba(245,158,11,0.08)",
    border: "1px solid rgba(245,158,11,0.2)",
    borderRadius: 12,
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(2),
  },
  stepText: { color: "rgba(255,255,255,0.8)", fontSize: 13 },
}));

const PartnerRegister = ({ onRegister, onGoLogin }) => {
  const classes = useStyles();
  const [form, setForm] = useState({ name: "", email: "", phone: "", document: "", password: "", pixKey: "" });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) { toast.error("Nome, e-mail e senha são obrigatórios."); return; }
    if (form.password.length < 6) { toast.error("Senha deve ter pelo menos 6 caracteres."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/partners/register", form);
      localStorage.setItem("partnerToken", data.token);
      localStorage.setItem("partnerData", JSON.stringify(data.partner));
      toast.success("Cadastro realizado! Aguarde a aprovação do seu cadastro.");
      onRegister(data.partner);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erro ao criar cadastro.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.root}>
      <Paper className={classes.paper} elevation={0}>
        <Box textAlign="center" mb={3}>
          <Typography className={classes.title}>Torne-se um Representante</Typography>
          <Typography variant="body2" style={{ color: "rgba(255,255,255,0.6)" }}>
            Indique clientes para a ChatPrime e ganhe 50% da 1ª mensalidade de cada um
          </Typography>
        </Box>

        <div className={classes.stepCard}>
          <Typography className={classes.stepText}>
            🔗 <strong style={{ color: "#f59e0b" }}>Como funciona:</strong>{" "}
            Após o cadastro (pendente de aprovação), você recebe seu link único de indicação.
            Quando o cliente pagar a 1ª mensalidade, 50% é creditado automaticamente no seu saldo.
          </Typography>
        </div>

        <form onSubmit={handleSubmit}>
          <TextField label="Nome completo *" name="name" variant="outlined" fullWidth className={classes.input}
            value={form.name} onChange={handleChange} autoFocus />
          <TextField label="E-mail *" name="email" type="email" variant="outlined" fullWidth className={classes.input}
            value={form.email} onChange={handleChange} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField label="WhatsApp" name="phone" variant="outlined" fullWidth className={classes.input}
                value={form.phone} onChange={handleChange} placeholder="11999999999" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="CPF/CNPJ" name="document" variant="outlined" fullWidth className={classes.input}
                value={form.document} onChange={handleChange} />
            </Grid>
          </Grid>
          <TextField label="Chave PIX (para receber)" name="pixKey" variant="outlined" fullWidth className={classes.input}
            value={form.pixKey} onChange={handleChange} placeholder="CPF, e-mail, telefone ou chave aleatória" />
          <TextField
            label="Senha *"
            name="password"
            variant="outlined"
            fullWidth
            className={classes.input}
            value={form.password}
            onChange={handleChange}
            type={showPass ? "text" : "password"}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPass(!showPass)} size="small" style={{ color: "rgba(255,255,255,0.5)" }}>
                    {showPass ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <Button type="submit" fullWidth variant="contained" className={classes.btn} disabled={loading}>
            {loading ? <CircularProgress size={22} style={{ color: "#fff" }} /> : "Criar Conta de Representante"}
          </Button>
        </form>

        <Box mt={2} textAlign="center">
          <Typography variant="body2" style={{ color: "rgba(255,255,255,0.5)" }}>
            Já tem conta?{" "}
            <span className={classes.link} onClick={onGoLogin}>Entrar</span>
          </Typography>
        </Box>
      </Paper>
    </div>
  );
};

export default PartnerRegister;
